import { useHistory, useParams } from 'react-router';
import { Box } from '@chakra-ui/react';

import { QuoteStatus } from '__generated__/GQL';
import { getLocalizedErrorMessage } from 'common/errors';
import { getServerErrorCode } from 'common/errors/utils';
import { useCompanySettingsQuery } from 'common/hooks/useCompanySettings';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import ModalProvider from 'components/ModalProvider';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';
import { QuoteFormSchema } from 'features/Work/form/quoteForm.schema';
import {
  QuoteModalsProvider,
  useQuoteModals,
} from 'features/Work/pages/Quotes/hooks/useQuoteModals';
import { ConfirmType } from 'features/Work/types';

import * as routes from '../../../../routes';
import FormLayout from '../../components/FormLayout';
import { useCachedQuote } from '../../components/FormLayout/hooks/useCachedQuote';
import useUpdateQuote from '../../hooks/mutations/useUpdateQuote';
import { useRetrievedQuoteQuery } from '../../hooks/queries/useRetrievedQuoteQuery';
import { formatRetrievedQuote } from './helpers/formatRetrievedQuote';
import { formatUpdateMutationInput } from './helpers/formatUpdateMutationInput';
import locales from './locales';

interface RouteParams {
  quoteId: string;
}

const EditPage = () => {
  const params = useParams<RouteParams>();
  const history = useHistory();

  const { showToast } = useToast();
  const { openModal } = useQuoteModals();
  const { clearCachedQuote } = useCachedQuote();

  const [updateQuote] = useUpdateQuote();

  const { companySettings, ...companySettingsQuery } =
    useCompanySettingsQuery();

  const { retrievedQuote, ...retrievedQuoteQuery } = useRetrievedQuoteQuery(
    params.quoteId,
  );

  if (retrievedQuoteQuery.loading || companySettingsQuery.loading) {
    return (
      <Box
        backgroundColor="white"
        bottom={0}
        left={0}
        position="absolute"
        right={0}
        top={0}
        zIndex={1}
      >
        <Loader />
      </Box>
    );
  }

  if (
    retrievedQuoteQuery.error ||
    companySettingsQuery.error ||
    !retrievedQuote ||
    !companySettings
  ) {
    return <ErrorView />;
  }

  if (retrievedQuote.status !== QuoteStatus.Pending) {
    history.replace(routes.quotes);
  }

  const onClose = () => history.replace(routes.getQuote(params.quoteId));

  const onConfirm = async (quote: QuoteFormSchema, type: ConfirmType) => {
    try {
      const updateQuoteInput = formatUpdateMutationInput(quote, retrievedQuote);
      await updateQuote(updateQuoteInput);

      logEvent({
        name: 'Quote Edited',
        properties: { invoiceId: params.quoteId },
      });
      clearCachedQuote();

      if (type === 'save') {
        showToast({
          message: locales.success,
          type: 'success',
        });
        return;
      }
      history.replace(routes.getQuote(retrievedQuote.quoteId));
    } catch (err) {
      const serverErrorCode = getServerErrorCode(err);

      if (serverErrorCode !== undefined) {
        showToast({
          message:
            getLocalizedErrorMessage(serverErrorCode) ?? locales.defaultError,
          type: 'error',
        });
      }
    }
  };

  return (
    <ModalProvider>
      <FormLayout
        companySettings={companySettings}
        isEditMode
        onClose={onClose}
        onConfirm={onConfirm}
        onDelete={() =>
          openModal({
            name: 'delete',
            props: {
              ...retrievedQuote,
              onDeleteSuccess: () => history.replace(routes.quotes),
            },
          })
        }
        quote={formatRetrievedQuote(retrievedQuote)}
      />
    </ModalProvider>
  );
};

const EditPageWithProviders = () => (
  <QuoteModalsProvider>
    <EditPage />
  </QuoteModalsProvider>
);

export default EditPageWithProviders;
